import { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { VirtualAppointmentOverviewItem } from '../../../services/virtualAppointmentsOverview';
import { VirtualAppointmentCancelModal } from '../../virtual-appointments/VirtualAppointmentCancelModal';
import { VirtualAppointmentMarkDoneModal } from '../../virtual-appointments/VirtualAppointmentMarkDoneModal';
import { isOpenStatus } from '../../virtual-appointments/openVirtualAppointments';
import {
    CANCEL_VA_BUTTON,
    MARK_DONE_BUTTON
} from '../../virtual-appointments/virtualAppointmentActionStyles';
import { canCancelVirtualAppointment } from '../../virtual-appointments/virtualAppointmentCancelable';
import { useCancelVirtualAppointment } from '../../virtual-appointments/useCancelVirtualAppointment';
import { useVirtualAppointmentRowActions } from '../../virtual-appointments/useVirtualAppointmentRowActions';
import { summaryFromDto } from '../../virtual-appointments/virtualAppointmentSummary';

type FooterModal = 'cancel' | 'done' | null;

interface OverviewActionsFooterProps {
    item: VirtualAppointmentOverviewItem;
    /** Lets the overview mute its own Escape while a confirm modal is stacked on top. */
    onModalOpenChange: (open: boolean) => void;
}

// The modals outlive the buttons: after a 409 refresh the VA may no longer offer the action, yet
// the refusal must stay readable until the admin closes it.
export function OverviewActionsFooter({ item, onModalOpenChange }: OverviewActionsFooterProps) {
    const cancelAction = useCancelVirtualAppointment(item.patient.id, item.id);
    const { statusPending, statusError, setStatus, resetStatusError } =
        useVirtualAppointmentRowActions(item.patient.id, item.id);
    const [modal, setModal] = useState<FooterModal>(null);
    useEffect(() => {
        onModalOpenChange(modal !== null);
        return () => onModalOpenChange(false);
    }, [modal, onModalOpenChange]);
    const closeModal = () => {
        setModal(null);
        cancelAction.reset();
        resetStatusError();
    };
    const canMarkDone = isOpenStatus(item.status);
    const canCancel = canCancelVirtualAppointment(item.status);
    const busy = cancelAction.pending || statusPending;
    const typeTitle = summaryFromDto(item).typeTitle;

    return (
        <>
            {(canMarkDone || canCancel) && (
                <div className="shrink-0 border-t border-gray-200 px-5 py-3 flex justify-end gap-2">
                    {canMarkDone && (
                        <button
                            type="button"
                            onClick={() => setModal('done')}
                            disabled={busy}
                            className={MARK_DONE_BUTTON}
                        >
                            <CheckCircleIcon className="w-3.5 h-3.5" />
                            Mark as done
                        </button>
                    )}
                    {canCancel && (
                        <button
                            type="button"
                            onClick={() => setModal('cancel')}
                            disabled={busy}
                            className={CANCEL_VA_BUTTON}
                        >
                            <XCircleIcon className="w-3.5 h-3.5" />
                            Cancel VA
                        </button>
                    )}
                </div>
            )}
            {modal === 'done' && (
                <VirtualAppointmentMarkDoneModal
                    typeTitle={typeTitle}
                    submitting={statusPending}
                    apiError={statusError}
                    onConfirm={() => setStatus('done', closeModal)}
                    onClose={closeModal}
                />
            )}
            {modal === 'cancel' && (
                <VirtualAppointmentCancelModal
                    typeTitle={typeTitle}
                    submitting={cancelAction.pending}
                    apiError={cancelAction.error}
                    onSubmit={(reason) => cancelAction.cancel(reason, closeModal)}
                    onClose={closeModal}
                />
            )}
        </>
    );
}
